import { memo, useLayoutEffect } from 'react';
import type { Ctx } from '@reatom/framework';
import { createCtx } from '@reatom/framework';
import { setupBatch, useCtx, withBatching } from '@reatom/npm-react';
import { urlAtom, setupUrlAtomSettings } from '@reatom/url';
import { unstable_batchedUpdates } from 'react-dom';

import { createDevtools, DEVTOOLS_KEY } from '@/js/libs/reatom-devtools';
import { useLocation } from '@/js/libs/router';

setupBatch(unstable_batchedUpdates);
export const ctx: Ctx = withBatching(createCtx({ restrictMultipleContexts: false }));

setupUrlAtomSettings(ctx, () => new URL(window.location.href));

createDevtools(ctx);

// Read once: switching the flag reloads the page.
const isDevtoolsVisible = localStorage.getItem(DEVTOOLS_KEY) === 'true';

const UrlAtomMirror = memo(function UrlAtomMirror() {
  // subscribe to location changes
  useLocation();
  const reactCtx = useCtx();
  // Syncs after the commit, not during render: writing an atom notifies its subscribers at once,
  // and during render that means updating other components mid-render.
  useLayoutEffect(() => {
    if (reactCtx.get(urlAtom).href !== window.location.href) {
      urlAtom(reactCtx, new URL(window.location.href));
    }
  });

  return null;
});

/**
 * Mirrors the router's location into reatom's `urlAtom` so the current URL is visible in reatom
 * devtools, whose state snapshot renders an atom of that name as its `href`. Nothing else in the app
 * touches `urlAtom`, so without this mirror the URL never appears there.
 *
 * The mirror is one-way: `setupUrlAtomSettings` above is called without a `sync` callback, so writing
 * the atom never reaches `history` — navigation stays entirely with react-router.
 *
 * Mounting the mirror only while the devtools are visible keeps navigation free of its re-render for
 * everyone else.
 */
export const RouterDevtoolsSync = () => (isDevtoolsVisible ? <UrlAtomMirror /> : null);
